import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { searchMovies } from '@/services/tmdb';
import MovieCard from '@/components/ui/MovieCard';
import MovieModal from '@/components/ui/MovieModal'; // Consistent modal usage

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || '';
  const navigate = useNavigate();

  const [inputValue, setInputValue] = useState(queryParam);
  const [movies, setMovies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  const [selectedMovie, setSelectedMovie] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Sync input with URL param if it changes externally
  useEffect(() => {
    setInputValue(queryParam);
  }, [queryParam]);

  // Fetch Results when Query or Page changes
  useEffect(() => {
    const fetchResults = async () => {
      if (!queryParam) {
        setMovies([]);
        return;
      }

      setIsLoading(true);
      try {
        const data = await searchMovies(queryParam, page);
        setMovies(data.results);
        setTotalPages(Math.min(data.totalPages, 500)); // TMDb limit
      } catch (error) {
        console.error("Search failed:", error);
      } finally {
        setIsLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    fetchResults();
  }, [queryParam, page]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (inputValue.trim()) {
      setPage(1); // Reset to page 1 on new search
      setSearchParams({ q: inputValue.trim() });
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Modal Handlers
  const handleShowDetails = (movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedMovie(null), 300);
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisibleButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage + 1 < maxVisibleButtons) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={i === page ? "default" : "outline"}
          size="icon"
          onClick={() => handlePageChange(i)}
          className={cn(
            "w-10 h-10 rounded-full",
            i === page ? "bg-primary text-primary-foreground" : "bg-secondary text-gray-400 hover:text-white"
          )}
        >
          {i}
        </Button>
      );
    }
    return buttons;
  };

  return (
    <div className="min-h-screen pt-24 px-4 md:px-12 bg-background pb-24">
      {/* Search Input Section */}
      <div className="max-w-3xl mx-auto mb-12">
        <form onSubmit={handleSearchSubmit} className="relative group">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Busca películas, series, actores..."
            className="w-full bg-[#1a1d24] border border-white/10 text-white text-lg rounded-full py-4 pl-6 pr-14 focus:outline-none focus:border-blue-500/50 focus:bg-[#20242c] focus:ring-1 focus:ring-blue-500/20 transition-all placeholder:text-gray-500 shadow-xl"
            autoFocus
          />
          <button
            type="submit"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-blue-600 hover:bg-blue-500 rounded-full text-white transition-all shadow-lg shadow-blue-500/20 group-hover:scale-105"
          >
            <Search className="w-5 h-5" strokeWidth={3} />
          </button>
        </form>
      </div>

      {/* Results Title (Only if query exists) */}
      {queryParam && (
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-8">
          {isLoading ? 'Buscando resultados para:' : 'Resultados para:'} <span className="text-primary italic">"{queryParam}"</span>
        </h1>
      )}

      {/* Content Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-12 h-12 text-primary animate-spin" />
        </div>
      ) : (
        <>
          {movies.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-x-6 md:gap-y-10">
                {movies.map((movie, index) => (
                  <MovieCard
                    key={movie.id}
                    movie={movie}
                    onShowDetails={handleShowDetails}
                    index={index}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-16 pt-8 border-t border-border/50">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className="w-10 h-10 rounded-full bg-secondary text-white disabled:opacity-50"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>

                  <div className="flex gap-2">
                    {renderPaginationButtons()}
                  </div>

                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === totalPages}
                    className="w-10 h-10 rounded-full bg-secondary text-white disabled:opacity-50"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </>
          ) : (
            queryParam && !isLoading && (
              <div className="flex flex-col items-center justify-center py-20 text-center text-gray-400">
                <Search className="w-16 h-16 mb-4 opacity-20" />
                <p className="text-xl font-medium mb-2">No se encontraron resultados</p>
                <p className="text-sm">Intenta con otros términos o revisa la ortografía.</p>
              </div>
            )
          )}
        </>
      )}

      {/* Detail Modal */}
      <MovieModal
        movie={selectedMovie}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </div>
  );
};

export default SearchPage;
